import React from 'react'
import { NotificationTemplateListContext } from './notification-template-list-context'
import { AntdTableEmptyPlaceholder, Well } from '@pushly/aqe/lib/components'
import { getClassNames } from '../../../_utils/classnames'

import { FormOutlined, LoadingOutlined } from '@ant-design/icons'
import { useLoadableDataState } from '../../../hooks/use-loadable-data-state'
import { getTablePaginationConfig } from '../../../components/segment-list/helpers'
import { stripUndefined } from '../../../_utils/strip-undefined'
import { GetNamedNotificationTemplateResponse } from '@pushly/models/lib/structs/notifications'
import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationTemplateService } from '../../../services/notification-template'
import { AppService } from '../../../services'
import { Button } from '@pushly/aqe/lib/components'
import { NNTDataTableControlOptions, FilterKey, NotificationTemplateListProps } from './interfaces'
import { NotificationTemplateListHeader } from './notification-named-template-list-header'
import SwSimpleLinkPagination from '../../../components/sw-simple-link-pagination/sw-simple-link-pagination'
import { Key, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import { TableRowEntityDisplay } from '../../../components/table-row-entity-display/table-row-entity-display'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { WebBadge } from '../../../components/badges/web-badge'
import { NativeIosBadge } from '../../../components/badges/native-ios-badge'
import { NativeAndroidBadge } from '../../../components/badges/native-android-badge'
import { NotificationImageBadge } from '../../../components/badges/notification-image-badge'
import { NotificationSilentBadge } from '../../../components/badges/notification-silent-badge'
import { AiGeneratedBadge } from '../../../components/badges/ai-generated-badge'
import structuredClone from '@ungap/structured-clone'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { asCaslSubject } from '../../../stores/app-ability'
import { AppState } from '../../../stores/app'
import { Container } from 'typescript-ioc'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { Modal, Table, TablePaginationConfig } from 'antd'
import { NotificationNamedTemplate } from '@pushly/models/lib/structs'

const NotificationTemplateList = (props: NotificationTemplateListProps) => {
    const appState: AppState = Container.get(AppState)
    const appSvc = useService(AppService)
    const notifTemplateSvc = useService(NotificationTemplateService)
    const [dataSource, setDataSource] = useLoadableDataState<GetNamedNotificationTemplateResponse[]>({
        loading: true,
        data: [],
    })
    const [tableOptions, setTableOptions] = React.useState<NNTDataTableControlOptions>({
        paginationConfig: getTablePaginationConfig({
            pageSize: props.pageSize ?? 15,
        }),
        refreshing: false,
        filters: {
            search: props.defaultFilters?.search,
        },
    })

    React.useEffect(() => {
        resetList()
    }, [])

    React.useEffect(() => {
        setTableOptions((prev) => ({
            ...prev,
            refreshing: true,
            paginationConfig: {
                ...prev.paginationConfig,
                current: tableOptions.paginationConfig.current,
            },
        }))
        setDataSource({ loading: true })

        fetchNotificationTemplates()
    }, [...Object.values(tableOptions.filters), tableOptions.paginationConfig.current])

    const resetList = () => {
        setDataSource({ loading: true })
        setTableOptions({
            refreshing: true,
            previousRowIds: [],
            paginationConfig: {
                ...tableOptions.paginationConfig,
                current: 1,
            },
            filters: {
                search: props.defaultFilters?.search,
            },
        })

        fetchNotificationTemplates()
    }

    const fetchNotificationTemplates = async () => {
        const { filters } = tableOptions

        const apiOptions = {
            showLoadingScreen: false,
            cancellationKey: 'notif-template-list.fetch',
            query: stripUndefined({
                page: tableOptions.paginationConfig.current,
                limit: tableOptions.paginationConfig.pageSize,
                search: filters.search,
            }),
        }

        // DTO won't work for this table, either create new or use model from @pushly/models
        const currDataSource: GetNamedNotificationTemplateResponse[] = dataSource.data ?? []

        setTableOptions((prev) => ({
            ...prev,
            previousRowIds: currDataSource?.map((data) => {
                return data.id
            }),
        }))

        let nextDataSource: GetNamedNotificationTemplateResponse[] = []
        const tableControlChanges = {
            ...tableOptions,
            refreshing: false,
        }

        const response = await notifTemplateSvc.fetchNamedNotificationTemplatesByDomainId(props.domainId, apiOptions)
        if (!response.cancelled) {
            if (response.ok && response.data) {
                response.data.forEach((datum) => {
                    nextDataSource.push(datum)
                })

                tableControlChanges.paginationLinks = response.meta.links ?? tableControlChanges.paginationLinks
            } else if (response.error) {
                console.warn(response.error)
            }

            setTableOptions(tableControlChanges)
            setDataSource({ loading: false, data: nextDataSource })
        }
    }

    const renderTemplateName = (template: GetNamedNotificationTemplateResponse) => {
        const name = template.name
        const defaultChannel = template.template?.channels?.default
        const containsImage = defaultChannel?.image_url
        const isSilent = defaultChannel?.is_silent
        const channels = template.channels

        // Extract AI source and metadata
        const aiResults = defaultChannel?.auto_suggested_fields_results as Record<string, any> | null | undefined
        let aiSource = aiResults?.source
        let aiMetadata = aiResults?.source_metadata ?? aiResults?.sourceMetadata

        // Backward compatibility: Legacy templates created before the source field was added
        // may have AI-generated content (title, body, imageUrl, keywords) but no source field.
        // In this case, default to 'page_analyzer' since it was the only AI tool available at that time.
        if (
            aiResults &&
            !aiSource &&
            (aiResults.title || aiResults.body || aiResults.imageUrl || aiResults.keywords?.length)
        ) {
            aiSource = 'page_analyzer'
        }

        return (
            <TableRowEntityDisplay
                title={name}
                badges={
                    <>
                        {aiSource === 'pushly_ai' && (
                            <AiGeneratedBadge source={aiSource} sourceMetadata={aiMetadata} showMetadata={false} />
                        )}
                        {containsImage && <NotificationImageBadge />}
                        {isSilent && <NotificationSilentBadge />}
                        {channels?.includes(DeliveryChannel.WEB) && <WebBadge />}
                        {channels?.includes(DeliveryChannel.NATIVE_IOS) && <NativeIosBadge />}
                        {channels?.includes(DeliveryChannel.NATIVE_ANDROID) && <NativeAndroidBadge />}
                    </>
                }
            />
        )
    }

    const renderRowActions = (template: GetNamedNotificationTemplateResponse) => {
        const actions: any[] = [
            {
                text: 'Edit',
                icon: 'edit',
                onClick: () => handleTemplateEdit(template),
                template,
                altHref: buildEditUrl(template),
            },
            {
                text: 'Duplicate',
                icon: 'copy',
                onClick: () => handleTemplateDuplicate(template),
                template,
                altHref: buildDuplicateUrl(template),
            },
            {
                text: 'Archive',
                icon: 'folder',
                onClick: () => handleTemplateArchive(template),
                template,
            },
        ]

        return (
            <Button
                onClick={() => appSvc.route(buildNewNotifUrl(template))}
                size="small"
                altHref={buildNewNotifUrl(template)}
                actions={actions}
            >
                <span>New Notification</span>
            </Button>
        )
    }

    const buildNewNotifUrl = (data: GetNamedNotificationTemplateResponse): string => {
        const templateId = data.id
        const newRoute = `/notifications/new?template_id=${templateId}`

        return appSvc.routeWithin(props.level, newRoute, true)
    }

    const handleTemplateEdit = (template: GetNamedNotificationTemplateResponse) => {
        appSvc.route(buildEditUrl(template))
    }

    const buildEditUrl = (data: GetNamedNotificationTemplateResponse) => {
        let templateId = data.id

        return appSvc.routeWithin(props.level, `/notifications/templates/edit/${templateId}`, true)
    }

    const handleTemplateDuplicate = (template: GetNamedNotificationTemplateResponse) => {
        appSvc.route(buildDuplicateUrl(template))
    }

    const buildDuplicateUrl = (data: GetNamedNotificationTemplateResponse): string => {
        let templateId = data.id

        return appSvc.routeWithin(props.level, `notifications/templates/new?template_id=${templateId}`, true)
    }

    const handleTemplateArchive = (template: GetNamedNotificationTemplateResponse) => {
        const finalize = async () => {
            await notifTemplateSvc.destroyNamedTemplate(props.domainId!, template.id, {
                showLoadingScreen: true,
            })

            return resetList()
        }

        return new Promise((res) => {
            Modal.confirm({
                title: 'Archive Notification Template',
                content: (
                    <div>
                        <p>
                            This template will no longer be shown in the Notification Templates List page. Are you sure
                            you want to archive this template?
                        </p>
                    </div>
                ),
                okText: 'Yes, Archive Template',
                onOk: () => res(finalize()),
                cancelText: 'Cancel',
                onCancel: () => res(false),
            })
        })
    }

    const handleSearchClick = async (value: string) => {
        const { search: prevValue } = tableOptions.filters
        const emptyValue = !value.trim() && prevValue?.trim()

        if (!emptyValue || value.length > 2) {
            setTableOptions((prev) => ({
                ...prev,
                paginationConfig: {
                    ...prev.paginationConfig,
                    current: 1,
                },
                filters: {
                    ...prev.filters,
                    search: value,
                },
            }))
            await fetchNotificationTemplates()
        }
    }

    const handleFilterChange = async <FilterValue extends NNTDataTableControlOptions['filters'][FilterKey]>(
        key: FilterKey,
        value: FilterValue,
    ): Promise<void> => {
        const prevFilters = structuredClone(tableOptions.filters)

        const newFilters = {
            ...prevFilters,
            [key]: value,
        }

        setTableOptions((prev) => ({
            ...prev,
            filters: newFilters,
        }))
    }

    const handleTableChange = async (
        pagination: TablePaginationConfig,
        _filters: Record<string, Key[] | null>,
        _sorter:
            | SorterResult<GetNamedNotificationTemplateResponse>
            | SorterResult<GetNamedNotificationTemplateResponse>[],
        _extra: TableCurrentDataSource<GetNamedNotificationTemplateResponse>,
    ) => {
        setTableOptions((prev) => ({
            ...prev,
            paginationConfig: {
                ...prev.paginationConfig,
                current: pagination.current,
            },
        }))
        await fetchNotificationTemplates()
    }

    const canMutate = () => {
        return appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(SubjectEntity.NOTIFICATION, { domainId: props.domainId }),
        )
    }

    const showTableLoadingAnimation = dataSource.loading && tableOptions.refreshing

    let filtersHidden: FilterKey[] = []
    if (props.hideFilters === true) {
        filtersHidden = ['search']
    } else if (Array.isArray(props.hideFilters)) {
        filtersHidden = props.hideFilters
    } else if (props.hideFilters) {
        filtersHidden = [props.hideFilters]
    }

    return (
        <NotificationTemplateListContext.Provider
            value={{
                ...dataSource,
                ...tableOptions,
                onSearchClick: handleSearchClick,
                onFilterChange: handleFilterChange,
            }}
        >
            <Table<GetNamedNotificationTemplateResponse>
                className={getClassNames('notification-template-list-table', 'aqe-table-well', {
                    ['hide-title']: !props.title,
                    ['hide-pagination']: props.hidePagination,
                    // ['hide-actions']: !currentUser
                })}
                rowClassName="notification-template-list-row"
                size="small"
                dataSource={dataSource.data}
                rowKey={(r) => r?.id || 0}
                pagination={false}
                title={() => (
                    <NotificationTemplateListHeader
                        title={props.title}
                        filterSize={props.filterSize}
                        hideFilters={filtersHidden}
                    />
                )}
                footer={(currentTableData) => (
                    <SwSimpleLinkPagination
                        currentTableData={currentTableData.filter(
                            (item): item is NonNullable<typeof item> => item !== null,
                        )}
                        links={tableOptions.paginationLinks}
                        onPrev={async (_ev, _link, config) => {
                            setTableOptions((prev) => ({
                                ...prev,
                                paginationConfig: {
                                    ...prev.paginationConfig,
                                    current: config.current,
                                },
                            }))
                            await fetchNotificationTemplates()
                        }}
                        onNext={async (_ev, _link, config) => {
                            setTableOptions((prev) => ({
                                ...prev,
                                paginationConfig: {
                                    ...prev.paginationConfig,
                                    current: config.current,
                                },
                            }))
                            await fetchNotificationTemplates()
                        }}
                    />
                )}
                onChange={handleTableChange}
                locale={{
                    emptyText: <AntdTableEmptyPlaceholder text="No Templates" icon={<FormOutlined />} />,
                }}
                loading={
                    showTableLoadingAnimation && {
                        indicator: <LoadingOutlined spin={true} />,
                    }
                }
            >
                <Table.Column key="name" className="name" title="Name" render={renderTemplateName} />
                <Table.Column
                    key="description"
                    className="description"
                    dataIndex={['description']}
                    title="Description"
                    render={(description) => {
                        return <span>{description}</span>
                    }}
                />

                {canMutate() && (
                    <Table.Column
                        key="actions"
                        className="ntl-actions"
                        title="Actions"
                        align="right"
                        render={renderRowActions}
                    />
                )}
            </Table>
        </NotificationTemplateListContext.Provider>
    )
}

export default NotificationTemplateList
